<script setup lang="ts">
// composables
const { api } = useFeathers()

// stores
const userStore = useAuthStore()
const guideStore = useGuideStore()

// static
const onboardText1 = 'Welcome to the Phoenix Graphics Election Portal.'
const onboardText2 = 'Here you can setup elections, order election supplies, upload files, and view order details pertaining to your elections.'
const today = (new Date(Date.now() + (new Date()).getTimezoneOffset() * 60000)).toISOString().substring(0, 10)

// refs
const { user } = storeToRefs(userStore)
const {
  dialog,
  guides,
  onboard,
} = storeToRefs(guideStore)

// service params
const electionParams = computed(() => ({
  query: {
    CustomerAccount: user.value?.SelectedCustomer.Account || null,
    ElectionDate: { $gte: today },
    $sort: { ElectionDate: -1 },
  },
}))
// computed
const headerText = computed(() => onboard.value ? `Welcome ${user.value.FullName}!` : 'Guides')

// services
const elections = api.service('pge/elections').useFind(electionParams, { paginateOn: 'server' })
// zconsole.log(elections.data)
// methods
const { reset, startGuide } = guideStore
async function updateOnboarding(value: any) {
  const userID = user.value?.ID
  const settings = { ...user.value?.Settings || {}, onboard: !value }
  if (!userID)
    return

  await api.service('pge/users').patch(userID, { Settings: settings }, { query: {
    ChildCustomers: true,
    Customer: true,
    SelectedCustomer: true,
    SelectedCustomerParameters: true,
    UserGroup: true,
  } })
}
</script>

<template>
  <v-dialog
    v-model="dialog"
    persistent
    width="450px"
  >
    <BaseSheet
      color-header
      dividers
      no-padding
    >
      <template #header>
        {{ headerText }}
      </template>

      <div class="py-2 px-4">
        <p class="text-subtitle-1 font-weight-bold mb-2">
          {{ onboardText1 }}
        </p>
        <p class="mb-2">
          {{ onboardText2 }}
        </p>
        <template v-if="onboard">
          <p class="mb-2">
            This onboarding guide will help you get navigate this portal and outline the different features available to you.
          </p>
          <p class="mb-2">
            Click the "Start" button below to get started.
          </p>
          <v-checkbox
            label="Do not show again"
            @update:model-value="updateOnboarding"
          />
        </template>
        <p v-else>
          To review different functions of our system, select from one of the guides below:
        </p>
      </div>

      <template v-if="!onboard">
        <v-divider />
        <v-list>
          <template
            v-for="(guide, ind) in guides"
            :key="`guide-${ind}`"
          >
            <v-tooltip
              v-if="ind === 'orders' && elections.data.length < 1"
              location="left"
              text="Create an election first to start this guide"
            >
              <template #activator="{ props }">
                <div
                  v-bind="props"
                  class="w-100"
                >
                  <v-list-item
                    disabled
                    :subtitle="guide.subtitle"
                    :title="guide.title"
                    @click="startGuide(guide)"
                  >
                    <template #prepend>
                      <v-icon :icon="guide.icon" />
                    </template>
                  </v-list-item>
                </div>
              </template>
            </v-tooltip>
            <v-list-item
              v-else
              :subtitle="guide.subtitle"
              :title="guide.title"
              @click="startGuide(guide)"
            >
              <template #prepend>
                <v-icon :icon="guide.icon" />
              </template>
            </v-list-item>
          </template>
        </v-list>
      </template>

      <template #actions>
        <ButtonClose
          @click="reset"
        />
        <v-spacer />
        <ButtonNext
          v-if="onboard"
          text="Start"
          @click="startGuide(guides.features)"
        />
      </template>
    </BaseSheet>
  </v-dialog>
</template>
